import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import Button from '../../../shared/_TBComponents/Button';
import Icon from '../../../shared/_TBComponents/Icon';
import Text from '../../../shared/_TBComponents/Text';
import { showSuccessToast } from '../../../shared/utils/toastManager';
import BlackoutDatesTable from './presentational/tables/BlackoutDatesTable';
import AddBlackoutDateModal from './presentational/modals/AddBlackoutDateModal';
import ConfirmModal from './presentational/modals/ConfirmModal';
import { fetchBlackoutDates, deleteBlackoutDates, fetchCoaches } from '../ducks/navigatorActions';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #F5F5F5;
  overflow: hidden;
`;

const TopSection = styled.div`
  background: #FFF;
  padding: 16px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
`;

const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
`;

const BackLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: #77A7FF;
  font-size: 16px;
  font-weight: 700;
  font-family: inherit;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ActionButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  flex-shrink: 0;

  @media (max-width: 767px) {
    width: 100%;

    button {
      flex: 1;
    }
  }
`;

const BottomSection = styled.div`
  flex: 1;
  min-height: 0;
  background: #FFF;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ManageBlackoutDates = ({ backPath }) => {
  const history = useHistory();
  const dispatch = useDispatch();

  const accountCode = useSelector((state) => state.auth.data.currentUser?.accountCode);
  const isDeletingBlackoutDates = useSelector(
    (state) => state.navigator.ui.isDeletingBlackoutDates,
  );

  const [blackoutDateToDelete, setBlackoutDateToDelete] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const refetchBlackoutDates = () => dispatch(fetchBlackoutDates(accountCode));

  useEffect(() => {
    if (accountCode) {
      dispatch(fetchBlackoutDates(accountCode));
      dispatch(fetchCoaches(accountCode));
    }
  }, [accountCode, dispatch]);

  const confirmDelete = () => {
    dispatch(deleteBlackoutDates(accountCode, [blackoutDateToDelete.id], {
      onSuccess: () => {
        showSuccessToast('Blackout date deleted.');
        setBlackoutDateToDelete(null);
      },
    }));
  };

  return (
    <Container>
      <TopSection>
        <TitleGroup>
          <BackLink type='button' onClick={() => history.push(backPath)}>
            <Icon icon='left-arrow' size={12} color='#77A7FF' />
            Back to Manage
          </BackLink>
          <Text styledTextProps={{ fontSize: '24px', fontWeight: 900, color: '#1A1A1A' }}>
            Blackout Dates
          </Text>
        </TitleGroup>
        <ActionButtonGroup>
          <Button
            name='Blackout Dates'
            icon='plus'
            iconColor='#FFF'
            txtColor='#FFF'
            bgColor='#1A1A1A'
            borderColor='#1A1A1A'
            customBorderRadius='4px'
            styledTextProps={{ fontSize: '14px', fontWeight: 700, letterSpacing: '0.3px' }}
            styledWrapperProps={{ boxShadow: '0 1px 3px 0 rgba(48, 49, 51, 0.10), 0 0 1px 0 rgba(48, 49, 51, 0.05)' }}
            onClick={() => setIsAddModalOpen(true)}
          />
        </ActionButtonGroup>
      </TopSection>

      <BottomSection>
        <BlackoutDatesTable onDeleteBlackoutDate={setBlackoutDateToDelete} />
      </BottomSection>

      <AddBlackoutDateModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onCreated={refetchBlackoutDates}
      />

      <ConfirmModal
        isOpen={!!blackoutDateToDelete}
        onClose={() => setBlackoutDateToDelete(null)}
        onConfirm={confirmDelete}
        title='Delete Blackout Date'
        message='Are you sure you want to delete this blackout date? This action cannot be undone.'
        isLoading={isDeletingBlackoutDates}
      />
    </Container>
  );
};

ManageBlackoutDates.propTypes = {
  backPath: PropTypes.string,
};

ManageBlackoutDates.defaultProps = {
  backPath: '/dashboard/manage',
};

export default ManageBlackoutDates;
